import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, MapPin, Clock, MessageCircle } from "lucide-react";
import { PHONE, PHONE_LINK, WHATSAPP_LINK } from "../lib/data";

const LINKS = [
  { label: "Acasă", to: "/" },
  { label: "Servicii", to: "/servicii" },
  { label: "Echipă", to: "/echipa" },
  { label: "Cazuri", to: "/cazuri" },
  { label: "Despre noi", to: "/despre" },
  { label: "Recenzii", to: "/recenzii" },
  { label: "Contact", to: "/contact" },
];

export const MedasLogo = ({ light = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
    <span className={`font-brand text-[26px] lg:text-[30px] font-medium tracking-[0.24em] leading-none transition-opacity group-hover:opacity-75 ${light ? "text-[#DCC79A]" : "text-[#A6823B]"}`}>
      MEDAS
    </span>
    <span className="flex flex-col leading-none">
      <span className={`font-script text-xl lg:text-2xl ${light ? "text-[#DCC79A]" : "text-[#A6823B]"}`}>Dental Point</span>
      <span className={`text-[9px] font-semibold uppercase tracking-[0.32em] mt-1 ${light ? "text-white/50" : "text-[#8A7A5E]"}`}>Clinică Dentară</span>
    </span>
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-lg shadow-[#2A2521]/5 border-b border-[#EADFC6]" : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="hidden md:block bg-[#2A2521] text-[#DCC79A]" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between text-xs font-medium">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Str. Letea 25, Timișoara</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Luni–Vineri 08:00–20:00</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="topbar-whatsapp" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <MessageCircle className="w-3.5 h-3.5" /> Scrie-ne pe WhatsApp
            </a>
            <a href={PHONE_LINK} data-testid="topbar-phone" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" /> {PHONE}
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 h-[76px] flex items-center justify-between">
        <MedasLogo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace("ă", "a").replace(" ", "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#A6823B]" : "text-[#4A3F2E] hover:text-[#A6823B]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={PHONE_LINK}
            data-testid="header-phone-button"
            className="hidden sm:inline-flex items-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold text-sm px-5 py-2.5 rounded-full shadow-lg shadow-[#A6823B]/25 transition-all hover:-translate-y-0.5"
          >
            <Phone className="w-4 h-4" /> Programează-te · {PHONE}
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Meniu"
            className="lg:hidden w-10 h-10 rounded-full border border-[#EADFC6] bg-white flex items-center justify-center text-[#2A2521]"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-[#EADFC6]"
            data-testid="nav-mobile"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace("ă", "a").replace(" ", "-")}`}
                  className="py-3 text-[#2A2521] font-medium border-b border-[#F0E9D8] last:border-0"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
